import {Component, Input, OnInit} from '@angular/core';
import {Website} from "../../../website";
import * as fromStore from "../../../../../store";
import {Store} from "@ngrx/store";
import {EcommerceService} from "../../ecommerce.service";
import {FormControl, FormGroup} from "@angular/forms";
import {TaxRate} from "../tax-rate-table/mapper/tax-rate";
import {DbActiveModal} from "../../../../../modules/modal/modal-ref";
import {filter, first} from "rxjs/operators";
import {DbResponse} from "../../../../../shared/interceptors/db-response";
import {NotifierComponent} from "../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../shared/components/notifier/notification-type";
import {IDsDropdownOption} from "../../../../../shared/components/ds-dropdown/ds-dropdown-option";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";

@Component({
    selector: 'db-add-tax-rate',
    templateUrl: './add-tax-rate.component.html',
    styleUrls: ['./add-tax-rate.component.scss'],
    standalone: false
})
export class AddTaxRateComponent implements OnInit {
  @Input() website: Website;
  @Input() taxRate?: TaxRate;
  @Input() taxRateClass?: string;
  public loading: boolean;
  public loadingPage: boolean = true;
  public additionalCountries: any;
  public countriesList: IDsDropdownOption[] = [
    {value: '*', label: 'All'}
  ];
  public statesList: IDsDropdownOption[] = [
    {value: '*', label: 'All'}
  ];

  public addTaxRateForm: FormGroup;


  constructor(
      private readonly _activeModal: DbActiveModal,
      private readonly _googleAnalyticsService: GoogleAnalyticsService,
      private readonly _store: Store<fromStore.DbStoreState>,
      private _ecommerceService: EcommerceService
  ) { }

  ngOnInit() {
    this.addTaxRateForm = new FormGroup({
      country: new FormControl((this.taxRate?.country !== '*' ? this.taxRate?.country : '') || ''),
      state: new FormControl((this.taxRate?.state !== '*' ? this.taxRate?.state : '') || ''),
      postcode: new FormControl((this.taxRate?.postcode !== '*' ? this.taxRate?.postcode : '') || ''),
      city: new FormControl((this.taxRate?.city !== '*' ? this.taxRate?.city : '') || ''),
      rate: new FormControl(this.taxRate?.rate || '0'),
      name: new FormControl(this.taxRate?.name || ''),
      class: new FormControl(this.taxRate?.class || this.taxRateClass || 'standard'),
      priority: new FormControl(this.taxRate?.priority || '1'),
      compound: new FormControl(this.taxRate?.compound || false),
      shipping: new FormControl(this.taxRate?.shipping || false),
    });
    this._getCountries();
  }

  private _getCountries(): void {
    this._ecommerceService.getEcommerceCountriesList(this.website?.id)
        .pipe(filter((response: DbResponse): boolean => !!response),
            first())
        .subscribe((response: DbResponse): void => {
          if (response?.success) {
            this.additionalCountries = (response?.data?.dataKey);
            this.additionalCountries.forEach(country => {
              this.countriesList.push({ value: country?.code, label: country?.name, moreOptions: country?.states });
            });
            this.loadingPage = false;
            if (this.taxRate?.country && this.taxRate?.state) {
              this.chooseCountry(this.taxRate?.country);
              this.chooseState(this.taxRate?.state);
            }
          }
          else {
            NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
          }
        });
  }

  public chooseCountry(countryName, selectOption?: boolean): void {
    this.statesList = [
      {value: '*', label: 'All'}
    ];
    this.addTaxRateForm.get('state').setValue('');
    const states = this.additionalCountries.find(country => country?.code === countryName)?.states;
    states.forEach(state => {
      this.statesList.push({ value: state?.code, label: state?.name });
    });
    !!selectOption && this.addTaxRateForm.get('country').setValue(countryName);
  }

  public updateRate(taxRateId): void {
    this.loading = true;
    this._setEmptyValues();
    this._ecommerceService.updateTaxRates(this.website?.id, taxRateId, this.addTaxRateForm.value)
        .pipe(filter((response: DbResponse): boolean => !!response),
            first())
        .subscribe((response: DbResponse): void => {
          if (response.success && response?.data) {
            this._store.dispatch(new fromStore.LoadEcommerceTaxRates({websiteId: this.website?.id}));
            this._store.dispatch(new fromStore.LoadEcommerceGeneralSettings({websiteId: this.website?.id}));
            this._store.dispatch(new fromStore.LoadEcommerceTaxSettings({websiteId: this.website?.id}));
            NotifierComponent.notify(new Notification(response.message, NotificationType.SUCCESS));
            this.closeDialog();
          }
          else {
            NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
            this.loading = false;
          }
        });
  }

  private _setEmptyValues(): void {
    this.addTaxRateForm.value.country === '' && this.addTaxRateForm.get('country').setValue('*');
    this.addTaxRateForm.value.state === '' && this.addTaxRateForm.get('state').setValue('*');
    this.addTaxRateForm.value.city === '' && this.addTaxRateForm.get('city').setValue('*');
    this.addTaxRateForm.value.postcode === '' && this.addTaxRateForm.get('postcode').setValue('*');
  }

  public addTaxRate(): void {
    this.loading = true;
    this._setEmptyValues();
    this._ecommerceService.addTaxRates(this.website?.id, this.addTaxRateForm.value)
        .pipe(filter((response: DbResponse): boolean => !!response),
            first())
        .subscribe((response: DbResponse): void => {
          if (response.success && response?.data) {
            this._store.dispatch(new fromStore.LoadEcommerceTaxRates({websiteId: this.website?.id}));
            this._store.dispatch(new fromStore.LoadEcommerceGeneralSettings({websiteId: this.website?.id}));
            this._store.dispatch(new fromStore.LoadEcommerceTaxSettings({websiteId: this.website?.id}));
            NotifierComponent.notify(new Notification(response.message, NotificationType.SUCCESS));
            this.closeDialog();
          }
          else {
            NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
            this.loading = false;
          }
        });
  }

  private _sendEvent(label): void {
    const analyticsData = {
      eventCategory: 'Dashboard Action',
      eventAction: 'Ecommerce - Tax',
      eventLabel: label
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  public chooseState(stateName): void {
    this.addTaxRateForm.get('state').setValue(stateName);
  }

  public closeDialog(): void {
    this._activeModal.close();
  }



}
